import { type Editor } from '@tiptap/react'
import * as React from 'react'
import { MediaDialog } from '#app/components/media-browser/media-dialog.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { type MediaItem } from '#app/hooks/use-supabase-upload.ts'

interface ImageMediaBlockProps {
	editor: Editor
	close: () => void
}

export const ImageMediaBlock: React.FC<ImageMediaBlockProps> = ({
	editor,
	close,
}) => {
	const handleMediaSelect = (selectedMedia: MediaItem[]) => {
		if (selectedMedia.length > 0) {
			// Insert the selected images into TipTap
			const images = selectedMedia
				.filter((media) => media.url) // Filter out items without URLs
				.map((media) => ({
					src: media.url!, // Use the media URL (non-null asserted after filter)
					alt: media.altText || media.title || '',
					title: media.title || '',
				}))

			if (images.length > 0) {
				editor.commands.setImages(images)
			}
			close()
		}
	}

	return (
		<div className="space-y-4">
			<MediaDialog
				onChange={handleMediaSelect}
				multiple={true}
				buttonText="Select from Media Library"
				title="Select Images"
				uploadPath="blog"
				allowedMimeTypes={['image/*']}
				hidePreview={true} // Don't show preview in this context
				trigger={
					<Button type="button" className="w-full">
						<Icon name="image" />
						Browse Media Library
					</Button>
				}
			/>

			{/* Keep the URL option for external images */}
			<div className="relative">
				<div className="absolute inset-0 flex items-center">
					<span className="w-full border-t" />
				</div>
				<div className="relative flex justify-center text-xs uppercase">
					<span className="bg-background text-muted-foreground px-2">Or</span>
				</div>
			</div>

			<URLImageInput editor={editor} close={close} />
		</div>
	)
}

// Extract URL input to separate component (no form element)
const URLImageInput: React.FC<{ editor: Editor; close: () => void }> = ({
	editor,
	close,
}) => {
	const [link, setLink] = React.useState('')

	const handleSubmit = () => {
		if (link.trim()) {
			editor.commands.setImages([{ src: link.trim() }])
			close()
		}
	}

	return (
		<div className="space-y-2">
			<Label htmlFor="image-link">Or paste an image URL</Label>
			<div className="flex gap-2">
				<Input
					id="image-link"
					type="url"
					placeholder="https://example.com/image.jpg"
					value={link}
					onChange={(e) => setLink(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault()
							handleSubmit()
						}
					}}
				/>
				<Button type="button" onClick={handleSubmit} disabled={!link.trim()}>
					Insert
				</Button>
			</div>
		</div>
	)
}

export default ImageMediaBlock
